<script lang="ts">
	import { onMount } from 'svelte';
	import { documentationSearch } from '$lib/docs/search';
	import type { SearchResult, Language } from '$lib/docs/types';
	import { _ } from 'svelte-i18n';
	
	interface Props {
		locale: Language;
	}
	
	let { locale }: Props = $props();
	
	// State
	let searchQuery = $state('');
	let searchResults = $state<SearchResult[]>([]);
	let isOpen = $state(false);
	let selectedIndex = $state(-1);
	let searchInput = $state<HTMLInputElement | null>(null);
	let recentSearches = $state<string[]>([]);
	let isSearching = $state(false);
	
	// Load recent searches from localStorage
	onMount(() => {
		const saved = localStorage.getItem('doc-recent-searches');
		if (saved) {
			recentSearches = JSON.parse(saved).slice(0, 5);
		}
		
		// Global keyboard shortcut
		function handleGlobalKeydown(e: KeyboardEvent) {
			// Escape to close search (when modal is open)
			if (e.key === 'Escape' && isOpen) {
				e.preventDefault();
				closeSearch();
				return;
			}
			
			// Cmd/Ctrl + K to open search
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				openSearch();
			}
			
			// Forward slash to open search (when not in input)
			if (e.key === '/' && !isInputElement(e.target)) {
				e.preventDefault();
				openSearch();
			}
		}
		
		document.addEventListener('keydown', handleGlobalKeydown);
		
		return () => {
			document.removeEventListener('keydown', handleGlobalKeydown);
		};
	});
	
	// Search logic with debouncing
	let searchTimeout: NodeJS.Timeout;
	$effect(() => {
		clearTimeout(searchTimeout);
		
		if (searchQuery.trim()) {
			isSearching = true;
			searchTimeout = setTimeout(async () => {
				searchResults = await documentationSearch.search(searchQuery, locale);
				isSearching = false;
				selectedIndex = -1;
			}, 150);
		} else {
			searchResults = [];
			isSearching = false;
			selectedIndex = -1;
		}
	});
	
	function isInputElement(target: any): boolean {
		return target && (
			target.tagName === 'INPUT' ||
			target.tagName === 'TEXTAREA' ||
			target.contentEditable === 'true'
		);
	}
	
	function openSearch() {
		isOpen = true;
		setTimeout(() => searchInput?.focus(), 50);
	}
	
	function closeSearch() {
		isOpen = false;
		searchQuery = '';
		searchResults = [];
		selectedIndex = -1;
	}
	
	function handleKeydown(e: KeyboardEvent) {
		switch (e.key) {
			case 'Escape':
				closeSearch();
				break;
				
			case 'ArrowDown':
				e.preventDefault();
				selectedIndex = Math.min(selectedIndex + 1, searchResults.length - 1);
				scrollToSelected();
				break;
				
			case 'ArrowUp':
				e.preventDefault();
				selectedIndex = Math.max(selectedIndex - 1, -1);
				scrollToSelected();
				break;
				
			case 'Enter':
				e.preventDefault();
				if (selectedIndex >= 0 && searchResults[selectedIndex]) {
					navigateToResult(searchResults[selectedIndex]);
				} else if (searchResults.length > 0) {
					navigateToResult(searchResults[0]);
				}
				break;
		}
	}
	
	function scrollToSelected() {
		if (selectedIndex >= 0) {
			const element = document.querySelector(`.search-result[data-index="${selectedIndex}"]`);
			element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
		}
	}
	
	function navigateToResult(result: SearchResult) {
		// Save to recent searches
		saveRecentSearch(searchQuery);
		
		// Navigate to the result
		window.location.href = `/docs/${result.slug}`;
		closeSearch();
	}
	
	function saveRecentSearch(query: string) {
		const updated = [query, ...recentSearches.filter(q => q !== query)].slice(0, 5);
		recentSearches = updated;
		localStorage.setItem('doc-recent-searches', JSON.stringify(updated));
	}
	
	function handleRecentSearch(query: string) {
		searchQuery = query;
		searchInput?.focus();
	}
	
	function clearRecentSearches() {
		recentSearches = [];
		localStorage.removeItem('doc-recent-searches');
	}
	
	// Highlight matching text
	function highlightText(text: string, query: string): string {
		if (!query) return text;
		const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
		return text.replace(regex, '<mark>$1</mark>');
	}
</script>

<!-- Search Button -->
<button 
	class="search-button"
	onclick={openSearch}
	aria-label="Search documentation"
>
	<svg class="search-button__icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
		<path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
	</svg>
	<span class="search-button__text">{$_('docs.search.button')}</span>
	<kbd class="search-button__shortcut">⌘K</kbd>
</button>

<!-- Search Modal -->
{#if isOpen}
	<div class="search-modal" role="dialog" aria-modal="true">
		<!-- Backdrop -->
		<button 
			class="search-modal__backdrop" 
			onclick={closeSearch}
			aria-label="Close search"
			tabindex="-1"
		></button>
		
		<!-- Dialog -->
		<div class="search-modal__dialog">
			<!-- Search Input -->
			<div class="search-modal__header">
				<div class="search-modal__input-wrapper">
					<svg class="search-modal__input-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
						<path d="M14.386 14.386l4.0877 4.0877-4.0877-4.0877c-2.9418 2.9419-7.7115 2.9419-10.6533 0-2.9419-2.9418-2.9419-7.7115 0-10.6533 2.9418-2.9419 7.7115-2.9419 10.6533 0 2.9419 2.9418 2.9419 7.7115 0 10.6533z" stroke="currentColor" fill="none" stroke-width="2"/>
					</svg>
					<input
						bind:this={searchInput}
						bind:value={searchQuery}
						onkeydown={handleKeydown}
						type="text"
						class="search-modal__input"
						placeholder={$_('docs.search.placeholder')}
						autocomplete="off"
						spellcheck="false"
					/>
					{#if searchQuery}
						<button 
							class="search-modal__clear"
							onclick={() => searchQuery = ''}
							aria-label="Clear search"
						>
							<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
								<path d="M7 0C3.134 0 0 3.134 0 7s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm2.828 9.172a.5.5 0 01-.707.707L7 7.707 4.879 9.828a.5.5 0 01-.707-.707L6.293 7 4.172 4.879a.5.5 0 01.707-.707L7 6.293l2.121-2.121a.5.5 0 01.707.707L7.707 7l2.121 2.172z"/>
							</svg>
						</button>
					{/if}
					<button 
						class="search-modal__close"
						onclick={closeSearch}
						aria-label="Close search"
					>
						<kbd>ESC</kbd>
					</button>
				</div>
			</div>
			
			<!-- Search Results -->
			<div class="search-modal__body">
				{#if isSearching}
					<div class="search-modal__loading">
						<div class="search-modal__spinner"></div>
						<span>{$_('docs.search.loading')}</span>
					</div>
				{:else if searchResults.length > 0}
					<div class="search-modal__results">
						<div class="search-modal__results-header">
							<span class="search-modal__results-count">
								{$_(`docs.search.results.count${searchResults.length === 1 ? '_one' : '_other'}`, { values: { count: searchResults.length } })}
							</span>
						</div>
						<ul class="search-results">
							{#each searchResults as result, index}
								<li>
									<button
										class="search-result"
										class:search-result--selected={selectedIndex === index}
										data-index={index}
										onclick={() => navigateToResult(result)}
										onmouseenter={() => selectedIndex = index}
									>
										<div class="search-result__content">
											<div class="search-result__title">
												{@html highlightText(result.title, searchQuery)}
											</div>
											<div class="search-result__description">
												{@html highlightText(result.description, searchQuery)}
											</div>
											<div class="search-result__meta">
												<span class="search-result__section">{result.section}</span>
												{#if result.tags?.length}
													<span class="search-result__tags">
														{#each result.tags.slice(0, 2) as tag}
															<span class="search-result__tag">{tag}</span>
														{/each}
													</span>
												{/if}
											</div>
										</div>
										<svg class="search-result__arrow" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
											<path d="M6.22 3.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 010-1.06z"/>
										</svg>
									</button>
								</li>
							{/each}
						</ul>
					</div>
				{:else if searchQuery}
					<div class="search-modal__empty">
						<svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor" opacity="0.2">
							<path d="M38.86 36.74L30.12 28a13.5 13.5 0 10-2.12 2.12l8.74 8.74a1.5 1.5 0 002.12-2.12zM6 19.5a13.5 13.5 0 1127 0 13.5 13.5 0 01-27 0z"/>
						</svg>
						<p>{$_('docs.search.results.empty', { values: { query: searchQuery } })}</p>
						<span>{$_('docs.search.results.emptyHint')}</span>
					</div>
				{:else if recentSearches.length > 0}
					<div class="search-modal__recent">
						<div class="search-modal__recent-header">
							<span>{$_('docs.search.recent.title')}</span>
							<button 
								class="search-modal__recent-clear"
								onclick={clearRecentSearches}
							>
								{$_('docs.search.recent.clear')}
							</button>
						</div>
						<ul class="search-modal__recent-list">
							{#each recentSearches as query}
								<li>
									<button
										class="search-modal__recent-item"
										onclick={() => handleRecentSearch(query)}
									>
										<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
											<path d="M7 0C3.134 0 0 3.134 0 7s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm0 12.6c-3.094 0-5.6-2.506-5.6-5.6S3.906 1.4 7 1.4s5.6 2.506 5.6 5.6-2.506 5.6-5.6 5.6zm.7-8.4H6.3v3.5L9.1 9.1l.7-.7L7.7 7V3.2z"/>
										</svg>
										{query}
									</button>
								</li>
							{/each}
						</ul>
					</div>
				{:else}
					<div class="search-modal__placeholder">
						<p>{$_('docs.search.emptyState.text')}</p>
						<div class="search-modal__tips">
							<span>{$_('docs.search.emptyState.tips.title')}</span>
							<ul>
								<li>{$_('docs.search.emptyState.tips.tip1')}</li>
								<li>{@html $_('docs.search.emptyState.tips.tip2')}</li>
								<li>{@html $_('docs.search.emptyState.tips.tip3')}</li>
								<li>{@html $_('docs.search.emptyState.tips.tip4')}</li>
							</ul>
						</div>
					</div>
				{/if}
			</div>
			
			<!-- Footer -->
			<div class="search-modal__footer">
				<div class="search-modal__footer-hints">
					<span>{@html $_('docs.search.shortcuts.select')}</span>
					<span>{@html $_('docs.search.shortcuts.navigate')}</span>
					<span>{@html $_('docs.search.shortcuts.close')}</span>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	/* Search Button */
	.search-button {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 0.75rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-default);
		border-radius: 8px;
		color: var(--text-secondary);
		cursor: pointer;
		transition: all 0.2s ease;
		font-size: 0.875rem;
	}
	
	.search-button:hover {
		background: var(--bg-tertiary);
		border-color: var(--border-heavy);
		color: var(--text-primary);
	}
	
	.search-button__icon {
		width: 16px;
		height: 16px;
	}
	
	.search-button__shortcut {
		margin-left: auto;
		padding: 0.125rem 0.375rem;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: 4px;
		font-size: 0.75rem;
		font-family: monospace;
		color: var(--text-tertiary);
	}
	
	/* Search Modal */
	.search-modal {
		position: fixed;
		inset: 0;
		z-index: 9999;
		display: flex;
		align-items: flex-start;
		justify-content: center;
		padding: 10vh 1rem;
	}
	
	.search-modal__backdrop {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.5);
		backdrop-filter: blur(4px);
		border: none;
		cursor: pointer;
		padding: 0;
	}
	
	.search-modal__dialog {
		position: relative;
		width: 100%;
		max-width: 680px;
		max-height: 70vh;
		background: var(--bg-elevated);
		border-radius: 12px;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
		display: flex;
		flex-direction: column;
		overflow: hidden;
		animation: modalSlideIn 0.2s ease;
	}
	
	@keyframes modalSlideIn {
		from {
			opacity: 0;
			transform: translateY(-20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
	
	/* Search Input */
	.search-modal__header {
		border-bottom: 1px solid var(--border-light);
	}
	
	.search-modal__input-wrapper {
		display: flex;
		align-items: center;
		padding: 1rem;
		gap: 0.75rem;
	}
	
	.search-modal__input-icon {
		color: var(--text-tertiary);
		flex-shrink: 0;
	}
	
	.search-modal__input {
		flex: 1;
		background: none;
		border: none;
		font-size: 1.125rem;
		color: var(--text-primary);
		outline: none;
	}
	
	.search-modal__input::placeholder {
		color: var(--text-tertiary);
	}
	
	.search-modal__clear,
	.search-modal__close {
		padding: 0.25rem;
		background: none;
		border: none;
		color: var(--text-tertiary);
		cursor: pointer;
		border-radius: 4px;
		transition: all 0.2s ease;
	}
	
	.search-modal__clear:hover,
	.search-modal__close:hover {
		background: var(--bg-tertiary);
		color: var(--text-primary);
	}
	
	.search-modal__close kbd {
		padding: 0.25rem 0.5rem;
		background: var(--bg-tertiary);
		border: 1px solid var(--border-light);
		border-radius: 4px;
		font-size: 0.75rem;
		font-family: monospace;
	}
	
	/* Search Body */
	.search-modal__body {
		flex: 1;
		overflow-y: auto;
		min-height: 200px;
	}
	
	/* Loading State */
	.search-modal__loading {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 3rem;
		gap: 1rem;
		color: var(--text-tertiary);
	}
	
	.search-modal__spinner {
		width: 24px;
		height: 24px;
		border: 2px solid var(--border-light);
		border-top-color: var(--brand-primary);
		border-radius: 50%;
		animation: spin 0.6s linear infinite;
	}
	
	@keyframes spin {
		to { transform: rotate(360deg); }
	}
	
	/* Results */
	.search-modal__results-header {
		padding: 0.75rem 1rem;
		border-bottom: 1px solid var(--border-light);
		background: var(--bg-secondary);
	}
	
	.search-modal__results-count {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}
	
	.search-results {
		list-style: none;
		padding: 0;
		margin: 0;
	}
	
	.search-result {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		padding: 0.75rem 1rem;
		background: none;
		border: none;
		border-bottom: 1px solid var(--border-light);
		text-align: left;
		cursor: pointer;
		transition: background-color 0.1s ease;
	}
	
	.search-result:hover,
	.search-result--selected {
		background: var(--bg-tertiary);
	}
	
	.search-result--selected {
		box-shadow: inset 3px 0 0 var(--brand-primary);
	}
	
	.search-result__content {
		flex: 1;
		min-width: 0;
	}
	
	.search-result__title {
		font-weight: 500;
		color: var(--text-primary);
		margin-bottom: 0.25rem;
	}
	
	.search-result__description {
		font-size: 0.875rem;
		color: var(--text-secondary);
		line-height: 1.4;
		margin-bottom: 0.25rem;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
	
	.search-result__meta {
		display: flex;
		gap: 0.5rem;
		align-items: center;
	}
	
	.search-result__section {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}
	
	.search-result__tags {
		display: flex;
		gap: 0.25rem;
	}
	
	.search-result__tag {
		padding: 0.125rem 0.375rem;
		background: var(--bg-secondary);
		color: var(--text-tertiary);
		font-size: 0.75rem;
		border-radius: 4px;
		text-transform: uppercase;
	}
	
	.search-result__arrow {
		color: var(--text-tertiary);
		flex-shrink: 0;
	}
	
	.search-result:hover .search-result__arrow {
		color: var(--text-primary);
	}
	
	/* Highlight */
	.search-result :global(mark) {
		background: var(--brand-muted);
		color: var(--brand-primary);
		padding: 0;
		border-radius: 2px;
		font-weight: 500;
	}
	
	/* Empty State */
	.search-modal__empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 3rem;
		text-align: center;
		color: var(--text-tertiary);
		gap: 0.5rem;
	}
	
	.search-modal__empty p {
		margin: 0.5rem 0;
		color: var(--text-secondary);
	}
	
	.search-modal__empty span {
		font-size: 0.875rem;
	}
	
	/* Recent Searches */
	.search-modal__recent {
		padding: 1rem;
	}
	
	.search-modal__recent-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 0.75rem;
		color: var(--text-tertiary);
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}
	
	.search-modal__recent-clear {
		background: none;
		border: none;
		color: var(--text-tertiary);
		cursor: pointer;
		font-size: 0.75rem;
		text-transform: none;
		transition: color 0.2s ease;
	}
	
	.search-modal__recent-clear:hover {
		color: var(--text-primary);
	}
	
	.search-modal__recent-list {
		list-style: none;
		padding: 0;
		margin: 0;
	}
	
	.search-modal__recent-item {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		width: 100%;
		padding: 0.5rem;
		background: none;
		border: none;
		border-radius: 4px;
		text-align: left;
		color: var(--text-secondary);
		cursor: pointer;
		transition: all 0.2s ease;
	}
	
	.search-modal__recent-item:hover {
		background: var(--bg-tertiary);
		color: var(--text-primary);
	}
	
	/* Placeholder */
	.search-modal__placeholder {
		padding: 3rem;
		text-align: center;
		color: var(--text-tertiary);
	}
	
	.search-modal__placeholder p {
		margin-bottom: 1.5rem;
		color: var(--text-secondary);
	}
	
	.search-modal__tips {
		text-align: left;
		max-width: 300px;
		margin: 0 auto;
		font-size: 0.875rem;
	}
	
	.search-modal__tips span {
		font-weight: 500;
		color: var(--text-secondary);
		display: block;
		margin-bottom: 0.5rem;
	}
	
	.search-modal__tips ul {
		list-style: disc;
		padding-left: 1.25rem;
		margin: 0;
	}
	
	.search-modal__tips li {
		margin-bottom: 0.25rem;
	}
	
	
	/* Footer */
	.search-modal__footer {
		padding: 0.75rem 1rem;
		border-top: 1px solid var(--border-light);
		background: var(--bg-secondary);
	}
	
	.search-modal__footer-hints {
		display: flex;
		gap: 1rem;
		font-size: 0.75rem;
		color: var(--text-tertiary);
	}
	
	
	/* Mobile */
	@media (max-width: 640px) {
		.search-modal {
			padding: 0;
		}
		
		.search-modal__dialog {
			max-width: 100%;
			max-height: 100vh;
			height: 100vh;
			border-radius: 0;
		}
		
		.search-button__text {
			display: none;
		}
	}
</style>